import React, { ReactElement, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OrganizacnaSkupinaEnum } from 'ciselniky-frontend-ts-axios';
import routes from '../../../routing/routes';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { useClient } from '../../../context/ClientStatusContext';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import MpkRequest from '../../../model/pracovnyKrok/MpkRequest';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import MpkStartForm from './component/MpkStartForm';
import MpkStartButtons from './component/MpkStartButtons';
import { startMpk } from '../../../client/pracovnyKrok/mpkClient';
import { configMpk } from '../../../client/admin/mpkConfigClient';
import { isToday } from '../../../function/pripomienkovanieFunctions';
import LegislativnyMaterialDetail
  from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import useOrganizacie from '../../../hooks/useOrganizacie';

interface Props {
  materialDetail: LegislativnyMaterialDetail;
  config?: boolean;
  admin?: boolean;
}

const MpkStart = ({ materialDetail, config = false, admin = false }: Props): ReactElement => {
  const now = new Date();
  const {
    type, year, index, pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();

  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();

  const [mpkRequest, setMpkRequest] = useState<MpkRequest>(
      {
        skratene: config ? materialDetail.skrateneMpk : false,
        dalsiPripomienkovatelia: config ? materialDetail.dalsiPripomienkovatelia : [],
        startDate: config ? materialDetail.datumZaciatkuPkMpk : now,
        endDate: config ? materialDetail.datumUkonceniaPkMpk : now,
        dovodSkratenia: config ? materialDetail.dovodSkratenehoMpk : '',
      } as MpkRequest,
  );

  const updateField: SafeParameterSetter<MpkRequest> = (fieldName, value) => {
    setMpkRequest(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  const {
    wrapClient: mpkClient,
    status: mpkStatus,
  } = useClient(config ? configMpk : startMpk);

  const handleMpk = async (): Promise<void> => {
    if (config) {
      await mpkClient(type, year, index, mpkRequest);
    } else {
      await mpkClient(type, year, index, pracovnyKrokId, mpkRequest);
    }
    navigate(routes.procesy.detail.withParams(type, year, index));
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const orgSkupinaPripomienkovatel = materialDetail?.typ?.kod === 'PoslaneckyNavrhZakon'
    ? OrganizacnaSkupinaEnum.PripomienkovatelPoslaneckyNavrhZakona
    : OrganizacnaSkupinaEnum.Pripomienkovatel;

  const {
    organizacie: povinniPripomienkovatelList,
    organizacieStatus: povinniPripomienkovatelListStatus,
  } = useOrganizacie(orgSkupinaPripomienkovatel, true);

  return (
    <>
      <MpkStartForm
        materialDetail={materialDetail}
        mpkRequest={mpkRequest}
        onUpdateField={updateField}
        pripomienkovatelList={povinniPripomienkovatelList}
        pripomienkovatelListLoading={povinniPripomienkovatelListStatus.loading}
        getFieldErrorMessage={getFieldErrorMessage}
        config={config}
        admin={admin}
      />
      <MpkStartButtons
        startDateIsToday={isToday(mpkRequest.startDate)}
        onClick={handleMpk}
        loading={mpkStatus.loading}
        config={config}
      />
    </>
  );
};

export default MpkStart;
